import { Connection} from '@models/clients/clients.details/connection';
import { ClientRepo } from '@services/clients/client.repo';
import { AuthService } from '@common/auth.service';

export class AddConnectionModalController {
    public client: any;
    public connection: Partial<Connection> = {};
    public dropdownOptions: any = {};
    public isAuthorisedSignatoryContext: boolean = false;
    public isEditMode: boolean = false;
    public isLoading: boolean = false;
    public title: string = 'Add Connection';
    public acceptButtonText: string = 'Save';
    public cancelButtonText: string = 'Cancel';

    /** Add flow: user links to an existing client via selection list (/selection). */
    public useExistingClient: boolean = false;
    public clients: any[] = [];
    public selectedSearchClient: any = null;
    public clientSearchText: string = '';
    public isLoadingClients: boolean = false;
    public populatedFromExistingClient: boolean = false;

    public fieldErrors: { [key: string]: boolean } = {};
    public showValidationErrors: boolean = false;

    public connectionCategoryOptions: any[] = [];
    public connectionTypeOptions: any[] = [];
    public relativeTypeOptions: any[] = [];
    public titleOptions: any[] = [];
    public statusOptions: any[] = [];

    /** @ngInject */
    constructor(
        private $mdDialog: angular.material.IDialogService,
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private clientRepo: ClientRepo,
        private notifyjs: (msg: string, type?: string) => void,
        public authService: AuthService
    ) {}

    /** Client number for the linked existing client (after pick or from saved connection). */
    public linkedClientNumber(): string | null {
        const ind = this.connection.existingClientNumberIndividual;
        const inst = this.connection.existingClientNumberInstitution;
        if (ind && String(ind).trim()) {
            return String(ind).trim();
        }
        if (inst && String(inst).trim()) {
            return String(inst).trim();
        }
        if (this.selectedSearchClient && this.selectedSearchClient.clientNumber) {
            return String(this.selectedSearchClient.clientNumber).trim();
        }
        return null;
    }

    public $onInit(): void {
        if (this.connection && this.connection.id) {
            this.isEditMode = true;
            this.title = 'Edit Connection';
            if (this.isLinkedConnection()) {
                this.useExistingClient = true;
                this.populatedFromExistingClient = true;
            }
        } else {
            this.connection = {
                status: 'active',
                selected: false
            } as Partial<Connection>;
        }

        this.$scope.$watch(() => this.connection.connectionCategory, (newCategory) => {
            if (newCategory) {
                this.initializeBankFields();
            }
        });

        this.$scope.$watch(() => this.connection.firstName, (newValue) => {
            if (newValue && String(newValue).trim() !== '') {
                this.clearFieldError('firstName');
            }
        });

        this.$scope.$watch(() => this.connection.lastName, (newValue) => {
            if (newValue && String(newValue).trim() !== '') {
                this.clearFieldError('lastName');
            }
        });

        if (this.isEditMode && this.isLinkedConnection()) {
            this.loadClientsForSelection(() => this.syncSelectedSearchClientForLinkedEdit());
        } else {
            this.loadClientsForSelection();
        }
    }

    private syncSelectedSearchClientForLinkedEdit(): void {
        const num = String(
            this.connection.existingClientNumberIndividual ||
                this.connection.existingClientNumberInstitution ||
                ''
        ).trim();
        if (!num) {
            return;
        }
        let match = this.clients.find((c) => String(c.clientNumber).trim() === num);
        if (!match) {
            const display =
                [this.connection.firstName, this.connection.lastName]
                    .filter(Boolean)
                    .join(' ')
                    .trim() || num;
            match = { clientNumber: num, name: display, status: '' };
            this.clients = [match, ...this.clients];
        }
        this.selectedSearchClient = match;
    }

    /** Connection row stores a link to an individual or institution client number. */
    public isLinkedConnection(): boolean {
        return !!(
            (this.connection.existingClientNumberIndividual &&
                String(this.connection.existingClientNumberIndividual).trim()) ||
            (this.connection.existingClientNumberInstitution &&
                String(this.connection.existingClientNumberInstitution).trim())
        );
    }

    /** Name/address/contact fields come from the client record (read-only UI). */
    public isMirroredFieldsReadOnly(): boolean {
        if (!this.useExistingClient) {
            return false;
        }
        if (this.isEditMode) {
            return this.isLinkedConnection();
        }
        return true;
    }

    public willSaveAsLinkedClient(): boolean {
        if (!this.useExistingClient) {
            return false;
        }
        if (
            this.connection.existingClientNumberIndividual ||
            this.connection.existingClientNumberInstitution
        ) {
            return true;
        }
        return !this.isEditMode && this.populatedFromExistingClient;
    }

    public onUseExistingClientChange(): void {
        if (this.useExistingClient) {
            const afterClientsReady = (): void => {
                if (this.isEditMode && this.isLinkedConnection()) {
                    this.syncSelectedSearchClientForLinkedEdit();
                }
            };
            if (this.isLoadingClients) {
                const unwatch = this.$scope.$watch(
                    () => this.isLoadingClients,
                    (loading: boolean) => {
                        if (!loading) {
                            unwatch();
                            afterClientsReady();
                        }
                    }
                );
            } else if (!this.clients.length) {
                this.loadClientsForSelection(afterClientsReady);
            } else {
                afterClientsReady();
            }
        } else {
            this.selectedSearchClient = null;
            this.clientSearchText = '';
            this.populatedFromExistingClient = false;
            this.connection.existingClientNumberIndividual = undefined;
            this.connection.existingClientNumberInstitution = undefined;
            if (!this.isEditMode) {
                this.connection.firstName = '';
                this.connection.middleName = '';
                this.connection.lastName = '';
                this.connection.dateOfBirth = undefined;
                this.connection.title = undefined;
                this.connection.emailAddress = '';
                this.connection.residentialAddress = '';
                this.connection.businessPhone = '';
                this.connection.mobilePhone = '';
                this.connection.residencePhone = '';
                this.connection.tanNumber = '';
            }
        }
    }

    public onClientDropdownOpen(): void {
        this.clientSearchText = '';
    }

    public loadClientsForSelection(onLoaded?: () => void): void {
        this.isLoadingClients = true;
        this.clientRepo
            .getClientsForSelection()
            .then((res) => {
                const selections =
                    (res.data as Array<{ id: number; clientNumber: string; displayName: string }>) || [];
                const host = this.client && this.client.clientNumber;
                const hostStr = host ? String(host).trim() : '';
                this.clients = selections
                    .filter((c) => c.displayName && String(c.displayName).trim() !== '')
                    .filter((c) => !hostStr || String(c.clientNumber).trim() !== hostStr)
                    .map((c) => ({
                        clientNumber: c.clientNumber,
                        name: c.displayName,
                        status: 'Active'
                    }));
                if (onLoaded) {
                    onLoaded();
                }
            })
            .catch(() => {
                this.notifyjs('Failed to load clients', 'error');
            })
            .finally(() => {
                this.isLoadingClients = false;
            });
    }

    public clientSearchFilter = (client: any): boolean => {
        if (!this.clientSearchText || !String(this.clientSearchText).trim()) {
            return true;
        }
        const t = String(this.clientSearchText).toLowerCase().trim();
        const name = (client.name || '').toLowerCase();
        const num = (client.clientNumber || '').toLowerCase();
        return name.indexOf(t) !== -1 || num.indexOf(t) !== -1;
    };

    public onExistingClientSelected(): void {
        if (!this.selectedSearchClient || !this.selectedSearchClient.clientNumber) {
            return;
        }
        this.isLoading = true;
        this.clientRepo
            .getClient(this.selectedSearchClient.clientNumber)
            .then((res) => {
                this.applyClientDetailsToConnection(res.data);
                this.populatedFromExistingClient = true;
            })
            .catch(() => {
                this.notifyjs('Failed to load client details', 'error');
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    private formatDateForPicker(v: any): string | undefined {
        if (v == null || v === '') {
            return undefined;
        }
        if (typeof v === 'string') {
            return v.length >= 10 ? v.substring(0, 10) : v;
        }
        if (v instanceof Date) {
            return v.toISOString().substring(0, 10);
        }
        return undefined;
    }

    private applyClientDetailsToConnection(d: any): void {
        const clientType = (d.clientType || '').toString().toLowerCase();
        const isInstitution = clientType === 'institution';

        if (!isInstitution) {
            this.connection.existingClientNumberIndividual = d.clientNumber;
            this.connection.existingClientNumberInstitution = undefined;
            this.connection.firstName = d.firstName || '';
            this.connection.middleName = d.middleName || '';
            this.connection.lastName = d.lastName || '';
            this.connection.dateOfBirth = this.formatDateForPicker(d.profileDateOfBirth);
            this.connection.title = d.contactTitle != null ? d.contactTitle : undefined;
            // Match Profile E-Mail (emailAddress1), same as connections list/API
            this.connection.emailAddress = d.emailAddress1 || d.emailAddress || '';
            this.connection.residentialAddress = d.profileResidentialAddress || '';
            this.connection.businessPhone =
                d.profileBusinessPhoneNumber || d.businessPhone || '';
            this.connection.mobilePhone = d.mobilePhone || d.profileMobilePhone || '';
            this.connection.residencePhone = d.residencePhone1 || '';
            this.connection.tanNumber = d.tan || '';
        } else {
            this.connection.existingClientNumberInstitution = d.clientNumber;
            this.connection.existingClientNumberIndividual = undefined;
            const orgName = (d.name || '').trim() || 'Unknown';
            this.connection.firstName = orgName;
            this.connection.lastName = '';
            this.connection.middleName = '';
            this.connection.dateOfBirth = this.formatDateForPicker(d.dateofIncorporation);
            this.connection.title = undefined;
            this.connection.emailAddress = d.emailAddress1 || d.emailAddress || '';
            this.connection.residentialAddress = d.profileRegisteredAddress || '';
            this.connection.businessPhone = d.businessPhone || d.telephone1 || '';
            this.connection.mobilePhone = '';
            this.connection.residencePhone = '';
            this.connection.tanNumber = d.tan || '';
        }
        this.initializeBankFields();
    }

    public isRelativeConnection(): boolean {
        return this.connection.connectionCategory === 3;
    }

    public isOtherRelativeType(): boolean {
        return this.connection.typeOfRelatedParty === 5;
    }

    public isBankConnection(): boolean {
        return this.connection.connectionCategory === 4;
    }

    public initializeBankFields(): void {
        if (this.isBankConnection() && this.client) {
            if (this.client.selectedRelationshipManager) {
                this.connection.relationshipManager = this.client.selectedRelationshipManager.displayName;
            } else if (this.client.relationshipManagerName) {
                this.connection.relationshipManager = this.client.relationshipManagerName;
            }
            if (this.client.nameOfPrivateBank) {
                this.connection.privateBanker = this.client.nameOfPrivateBank;
            }
        }
    }

    private buildSavePayload(): Partial<Connection> {
        if (!this.willSaveAsLinkedClient()) {
            return { ...this.connection };
        }
        return {
            id: this.connection.id,
            status: this.connection.status,
            selected: this.connection.selected,
            connectionCategory: this.connection.connectionCategory,
            existingClientNumberIndividual: this.connection.existingClientNumberIndividual || undefined,
            existingClientNumberInstitution: this.connection.existingClientNumberInstitution || undefined,
            typeOfRelatedParty: this.connection.typeOfRelatedParty,
            otherRelatedPartyType: this.connection.otherRelatedPartyType,
            nameOfRelationshipManager: this.connection.nameOfRelationshipManager,
            comments: this.connection.comments,
            firstName: undefined,
            middleName: undefined,
            lastName: undefined,
            maidenName: undefined,
            dateOfBirth: undefined,
            title: undefined,
            nationality: undefined,
            emailAddress: undefined,
            residentialAddress: undefined,
            businessPhone: undefined,
            mobilePhone: undefined,
            residencePhone: undefined,
            tanNumber: undefined
        } as Partial<Connection>;
    }

    public handleAccept(): void {
        if (!this.validateForSave()) {
            this.showValidationErrors = true;
            return;
        }
        this.$mdDialog.hide(this.buildSavePayload());
    }

    public handleCancel(): void {
        this.$mdDialog.cancel();
    }

    public handleDelete(): void {
        if (confirm('Are you sure you want to delete this connection?')) {
            this.$mdDialog.hide({
                connection: this.connection,
                action: 'delete'
            });
        }
    }

    public isFieldInvalid(fieldName: string): boolean {
        return this.showValidationErrors && this.fieldErrors[fieldName];
    }

    public clearFieldError(fieldName: string): void {
        if (this.fieldErrors[fieldName]) {
            this.fieldErrors[fieldName] = false;
        }
    }

    private validateForSave(): boolean {
        this.fieldErrors = {};
        if (!this.connection.connectionCategory) {
            this.fieldErrors['connectionCategory'] = true;
            this.notifyjs('Connection category is required', 'error');
            return false;
        }
        if (this.useExistingClient && !this.willSaveAsLinkedClient()) {
            this.notifyjs('Please select an existing client', 'error');
            return false;
        }
        if (this.willSaveAsLinkedClient()) {
            return true;
        }
        this.connection.existingClientNumberIndividual = undefined;
        this.connection.existingClientNumberInstitution = undefined;
        let ok = true;
        if (!this.connection.firstName || String(this.connection.firstName).trim() === '') {
            this.fieldErrors['firstName'] = true;
            ok = false;
        }
        if (!this.connection.lastName || String(this.connection.lastName).trim() === '') {
            this.fieldErrors['lastName'] = true;
            ok = false;
        }
        return ok;
    }
}

export const AddConnectionModalComponent: angular.IComponentOptions = {
    template: require('./add-connection-modal.html'),
    controller: AddConnectionModalController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<',
        connection: '<',
        dropdownOptions: '<',
        isAuthorisedSignatoryContext: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/connections/modals/add-connection-modal.ts